import cn from 'classnames';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ViewerNoteEditorContent } from '@shared/ui/index.ts';
import * as st from './styles.module.scss';
import './styles.css';

const removeHtmlIfImg = (html: string): string => {
  const indexOfImgTag = html.indexOf('<img');
  if (indexOfImgTag > -1) {
    return html.slice(0, indexOfImgTag);
  }
  return html;
};

type Props = {
  description: string;
  onShowMore?: () => void;
  className?: string;
  isAlwaysFull?: boolean;
  isSmallViewer?: boolean;
  buttonLabelOnFull?: string;
  isShouldHideButtonOnFull?: boolean;
  buttonLabelOnSmallWhenFirstImg?: string;
  buttonLabelOnSmallWhenFirstText?: string;
  buttonLabelClassName?: string;
  isOpenInit?: boolean;
};

const DescriptionViewer: React.FC<Props> = ({
  description,
  onShowMore,
  className,
  isAlwaysFull,
  isSmallViewer,
  buttonLabelOnSmallWhenFirstImg = 'ЧИТАТЬ ОПИСАНИЕ',
  buttonLabelOnSmallWhenFirstText = 'ЧИТАТЬ БОЛЬШЕ',
  isShouldHideButtonOnFull,
  buttonLabelOnFull = 'Скрыть текст',
  buttonLabelClassName,
  isOpenInit = false,
}) => {
  const [isFull, setIsFull] = useState(() => {
    return Boolean(isAlwaysFull || isOpenInit);
  });

  const canShowMore = useMemo(() => {
    const el = document.createElement('section');
    el.innerHTML = description;
    const isNeedShowMore = Boolean(
      // если есть картинка внутри
      el.getElementsByTagName('img').length ||
        el.children.length > 2 ||
        // если длинна текстового контекта > 30
        (el?.textContent && el?.textContent.length > 60)
    );
    !isAlwaysFull && isNeedShowMore && !isOpenInit && setIsFull(false);
    return isAlwaysFull || !description ? false : isNeedShowMore;
  }, [description, isAlwaysFull, isOpenInit]);

  const [img, setImg] = useState<string | null>(null);
  const [text, setText] = useState<string>('');

  useEffect(() => {
    const el = document.createElement('section');

    el.innerHTML = description?.trim();
    const childNodes = el.children;
    const firstNode = childNodes[0];
    const firstElement = firstNode?.children[0];

    const secondNode = childNodes[1];

    if (firstElement?.nodeName === 'IMG' && !isSmallViewer) {
      setImg(firstElement?.getAttribute('src'));
      let textContent = firstNode?.innerHTML;
      if (!textContent && secondNode && secondNode?.nodeName === 'P' && !secondNode?.children[0]) {
        textContent = secondNode?.innerHTML;
      }
      textContent && setText(removeHtmlIfImg(textContent));
    } else {
      const nodeName = firstNode?.nodeName;
      const innerHtml = firstNode?.innerHTML;

      if (!nodeName) {
        setText(description);
      }

      if (nodeName === 'P' || nodeName === '#text') {
        setText(removeHtmlIfImg(innerHtml));
      } else {
        if (nodeName === 'H1') {
          setText(removeHtmlIfImg(innerHtml));
        }
        if (nodeName === 'H2') {
          setText(removeHtmlIfImg(innerHtml));
        }
      }
    }
  }, [description]);

  const buttonLabelOnSmall = img ? buttonLabelOnSmallWhenFirstImg : buttonLabelOnSmallWhenFirstText;
  const showMoreButton = useMemo(
    () => (
      <button
        className={cn(st.btn, buttonLabelClassName, isShouldHideButtonOnFull && isFull && st.hide)}
        onClick={() => {
          if (onShowMore) {
            onShowMore();
          } else {
            setIsFull(!isFull);
            if (isFull) window.scrollTo(0, 0);
          }
        }}
      >
        {isFull ? buttonLabelOnFull : buttonLabelOnSmall}
      </button>
    ),
    [
      onShowMore,
      setIsFull,
      isFull,
      buttonLabelOnFull,
      isShouldHideButtonOnFull,
      buttonLabelOnSmall,
      buttonLabelClassName,
    ]
  );

  if (isFull) {
    return (
      <article className={cn('viewerWrapper', st.wrapper, className, img && st.firstImg)}>
        <ViewerNoteEditorContent
          htmlString={description}
          type={'all'}
          className={cn(st.editor, isFull && st.full)}
        />
        {canShowMore && showMoreButton}
      </article>
    );
  } else {
    return (
      <article className={cn(st.smallDescription, className)}>
        <div className={st.smallDescriptionTextWithButton}>
          {text && (
            <p
              dangerouslySetInnerHTML={text ? { __html: text } : undefined}
              style={{ marginBottom: 0 }}
              className={cn(st.smallDescriptionText, isSmallViewer && st.twoLines)}
            />
          )}
          {canShowMore && showMoreButton}
        </div>
      </article>
    );
  }
};

export default DescriptionViewer;
